'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { getMenuUrl } from 'header/services/menu'
import type { MenuEntry, MenuService } from 'header/types/menu'
import type { MenuServiceLegacy } from 'header/types/menu/menuLegacy'
import { useHeaderDispatch } from 'hooks/useHeaderContext/useHeaderDispatch'
import { useClientLabels } from 'labels/hooks/useClientLabels/useClientLabels'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useSetLabels } from 'labels/hooks/useSetLabels/useSetLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect, useMemo } from 'react'
import { fetcher } from 'services/rest/fetcher'
import useSWRImmutable from 'swr/immutable'
import { Device } from 'types/masterData'
import { useMatchMedia } from 'utils/matchMedia/hooks/useMatchMedia'

import { useMenus } from '../../hooks/useMenus'
import { adapterNewMenuService } from '../../utils/menu/adapterNewMenuService'
import { getLabelsIdsFromMenu } from '../../utils/menu/getLabelsIdsFromMenu'
import { MenuAdaptive } from './MenuAdaptiveLegacy/MenuAdaptive'

import mq from 'utils/matchMedia/styles/MatchMediaQueries.module.scss'

type RevalidateMenuProps = {
	data: MenuServiceLegacy | MenuService
	brand: string | null
	isNewServiceEnable: boolean
}

export function RevalidateMenu({
	data,
	brand,
	isNewServiceEnable,
}: RevalidateMenuProps) {
	const {
		country: { countryISO, languageISO },
		device: initialDevice,
		isBot,
		customDate,
	} = useMasterData()
	const dispatchHeader = useHeaderDispatch()
	const { open } = useMenus()
	const { labels: layoutLabels } = useLabels()
	const setLabels = useSetLabels()

	const { enabled: FflagHomeActivation } = useFeatureFlag(
		featureFlagKeys.HomeActivation
	)
	const { enabled: FflagEditActivation } = useFeatureFlag(
		featureFlagKeys.EditActivation
	)
	const { enabled: FflagCatalogActivation } = useFeatureFlag(
		featureFlagKeys.PlpVersion
	)

	const [isDesktop] = useMatchMedia([mq.islargeorgreater])
	const device = isDesktop ? Device.Desktop : Device.Mobile

	const shouldRevalidate = !isBot && open

	const { data: revalidateData } = useSWRImmutable<MenuService>(
		shouldRevalidate && initialDevice === device
			? getMenuUrl({
					country: countryISO,
					language: languageISO,
					isClient: true,
					device: initialDevice,
					customDate,
					isNewServiceEnable,
				})
			: null,
		fetcher
	)

	const { data: otherDeviceData } = useSWRImmutable<MenuService>(
		shouldRevalidate && !isNewServiceEnable && initialDevice !== device
			? getMenuUrl({
					country: countryISO,
					language: languageISO,
					isClient: true,
					device,
					customDate,
					isNewServiceEnable,
				})
			: null,
		fetcher
	)

	const menuData =
		(initialDevice === device ? revalidateData : otherDeviceData) || data

	const adaptMenuData = useMemo(
		() =>
			isNewServiceEnable
				? adapterNewMenuService(
						menuData as MenuService,
						device,
						FflagHomeActivation,
						FflagEditActivation,
						FflagCatalogActivation
					)
				: (menuData as MenuServiceLegacy),
		[menuData, device]
	)

	const labelIds = useMemo(
		() =>
			isNewServiceEnable
				? getLabelsIdsFromMenu(menuData.menus as MenuEntry[])
				: undefined,
		[menuData]
	)

	function getNotTranslatedKeys(keys: string[]): string[] {
		const notTranslatedKeys: string[] = []
		keys.forEach((labelId) => {
			if (!layoutLabels[labelId]) {
				notTranslatedKeys.push(labelId)
			}
		})
		return notTranslatedKeys
	}

	const notTranslatedLabelsIds = useMemo(
		() => labelIds && getNotTranslatedKeys(labelIds),
		[labelIds]
	)

	const { labels: labelsClient } = useClientLabels({
		keys: notTranslatedLabelsIds,
		namespaces: [],
		suspense: false,
		countryISO,
		languageISO,
	})

	useEffect(() => {
		if (layoutLabels && labelsClient) {
			setLabels({
				...layoutLabels,
				...labelsClient,
			})
		}
	}, [labelsClient])

	useEffect(() => {
		if (brand) {
			dispatchHeader({ brandId: brand })
		}
	}, [brand])

	return <MenuAdaptive data={adaptMenuData} />
}
